// ============================================================================
//  HR Module — Déclarations sociales et fiscales
//  Page : /hr/payroll/declarations
//  Trois onglets : IPRES mensuelle · DIPE mensuelle · Récap annuel
// ============================================================================

import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  FileText, Download, Building2, Calendar, AlertCircle,
  TrendingUp, Users, Hash, RefreshCw, ChevronDown,
} from 'lucide-react'
import { hrApi, type PayrollPeriod } from '@/modules/hr/api/hr.api'
import { useNotificationStore } from '@/stores/notification.store'
import { fmtXOF } from '@/lib/decimal'

const MONTHS_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
]

type Tab = 'ipres' | 'dipe' | 'recap'

export default function DeclarationsPage() {
  const [tab, setTab] = useState<Tab>('ipres')

  return (
    <div className="space-y-5 nx-fade-up">
      {/* HEADER */}
      <div>
        <h1 className="text-xl font-bold text-[var(--brand-dark)]">Déclarations sociales et fiscales</h1>
        <p className="text-sm text-[var(--tx3)] mt-1">
          Génération des déclarations IPRES, DIPE et récapitulatif annuel — exports CSV pour téléversement officiel
        </p>
      </div>

      {/* INFO */}
      <div className="bg-[#EFF6FF] border border-[var(--blue)]/30 rounded-xl p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-[var(--blue-d)] flex-shrink-0 mt-0.5" />
        <div className="text-sm text-[var(--blue-d)]">
          Les déclarations ne sont disponibles que pour les périodes <strong>APPROVED</strong>, <strong>PAID</strong> ou <strong>LOCKED</strong>.
          Le récapitulatif annuel agrège uniquement les périodes <strong>PAID</strong> et <strong>LOCKED</strong>.
        </div>
      </div>

      {/* ONGLETS */}
      <div className="border-b border-[var(--bd)]">
        <div className="flex gap-1">
          {[
            { id: 'ipres' as Tab, label: 'IPRES mensuelle', desc: 'Cotisations retraite' },
            { id: 'dipe'  as Tab, label: 'DIPE mensuelle',  desc: 'IRPP + TRIMF + CFCE' },
            { id: 'recap' as Tab, label: 'Récap annuel',     desc: 'État des salaires sur 12 mois' },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                tab === t.id
                  ? 'border-[var(--blue)] text-[var(--brand-dark)]'
                  : 'border-transparent text-[var(--tx3)] hover:text-[var(--tx2)]'
              }`}
            >
              <div>{t.label}</div>
              <div className="text-xs text-[var(--tx3)] font-normal mt-0.5">{t.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* CONTENU */}
      {tab === 'ipres' && <IpresTab />}
      {tab === 'dipe'  && <DipeTab />}
      {tab === 'recap' && <RecapTab />}
    </div>
  )
}

// ============================================================================
//  Sélecteur de période (réutilisé dans IPRES + DIPE)
// ============================================================================

function PeriodSelector({
  value,
  onChange,
}: {
  value: string | null
  onChange: (id: string) => void
}) {
  const { data: periodsData, isLoading } = useQuery({
    queryKey: ['payroll-periods-for-declarations'],
    queryFn:  () => hrApi.payroll.listPeriods({ page: 1 } as any),
  })

  const periods = useMemo(() => {
    return (periodsData?.periods ?? []).filter((p: PayrollPeriod) =>
      ['APPROVED', 'PAID', 'LOCKED'].includes(p.status)
    )
  }, [periodsData])

  if (isLoading) {
    return <div className="text-sm text-[var(--tx3)]">Chargement des périodes…</div>
  }

  if (periods.length === 0) {
    return (
      <div className="bg-[#FEF3C7] border border-[#F59E0B]/30 rounded-xl p-4 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-[#D97706]" />
        <p className="text-sm text-[#92400E]">
          Aucune période approuvée. Génère d'abord une période et fais-la valider.
        </p>
      </div>
    )
  }

  return (
    <div>
      <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">Période</label>
      <select
        value={value ?? ''}
        onChange={e => onChange(e.target.value)}
        className="w-full max-w-md px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:border-[var(--blue)]"
      >
        <option value="">— Sélectionner une période —</option>
        {periods.map((p: PayrollPeriod) => (
          <option key={p.id} value={p.id}>
            {MONTHS_FR[p.month - 1]} {p.year} ({p.status})
          </option>
        ))}
      </select>
    </div>
  )
}

// ============================================================================
//  Onglet IPRES
// ============================================================================

function IpresTab() {
  const [periodId, setPeriodId] = useState<string | null>(null)
  const notify = useNotificationStore(s => s.addNotification)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['declaration-ipres', periodId],
    queryFn:  () => hrApi.payroll.getDeclarationIpres(periodId!),
    enabled:  !!periodId,
  })

  const downloadCsv = () => {
    if (!data?.exportCsv) {
      notify({ type: 'error', message: 'Export CSV non disponible' })
      return
    }
    const blob = new Blob([data.exportCsv], { type: 'text/csv;charset=utf-8' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href     = url
    a.download = `IPRES-${data.periode}.csv`
    a.click()
    URL.revokeObjectURL(url)
    notify({ type: 'success', message: 'CSV téléchargé' })
  }

  return (
    <div className="space-y-4">
      <PeriodSelector value={periodId} onChange={setPeriodId} />

      {isLoading && (
        <div className="flex items-center justify-center h-40">
          <RefreshCw className="w-8 h-8 text-[var(--blue)] animate-spin" />
        </div>
      )}

      {isError && (
        <div className="bg-[#FEF2F2] border border-[#DC2626]/30 rounded-xl p-4 text-sm text-[#DC2626]">
          {(error as any)?.response?.data?.message || 'Erreur de chargement'}
        </div>
      )}

      {data && (
        <>
          {/* HEADER DECLARATION */}
          <div className="bg-[var(--bg)] border border-[var(--bd)] rounded-xl p-5 flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-[var(--sb-bg)] flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-base font-bold text-[var(--brand-dark)]">Déclaration IPRES — {data.periode}</h3>
                <p className="text-xs text-[var(--tx3)]">Échéance : {data.echeance}</p>
              </div>
            </div>
            <button
              onClick={downloadCsv}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[#2DAFD9] text-sm font-medium text-white rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              Télécharger CSV
            </button>
          </div>

          {/* TOTAUX */}
          <div className="grid grid-cols-3 gap-4">
            <StatCard label="Total salaires de base" value={fmtXOF(data.totaux.salaireBase)} color="var(--brand-dark)" />
            <StatCard label="Total cotisations salarié" value={fmtXOF(data.totaux.totalSalarie)} color="#7C3AED" />
            <StatCard label="Total cotisations patronal" value={fmtXOF(data.totaux.totalPatronal)} color="#D97706" />
          </div>

          {/* TABLEAU */}
          <div className="bg-white border border-[var(--bd)] rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--bd)] bg-[var(--bg)] text-xs text-[var(--tx3)] uppercase tracking-wider">
                    <th className="text-left  py-3 px-3 font-medium">Matricule</th>
                    <th className="text-left  py-3 px-3 font-medium">Employé</th>
                    <th className="text-left  py-3 px-3 font-medium">N° IPRES</th>
                    <th className="text-right py-3 px-3 font-medium">Salaire base</th>
                    <th className="text-right py-3 px-3 font-medium">RG salarié</th>
                    <th className="text-right py-3 px-3 font-medium">RG patronal</th>
                    <th className="text-right py-3 px-3 font-medium">RC salarié</th>
                    <th className="text-right py-3 px-3 font-medium">RC patronal</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--bd)]">
                  {(data.lignes as any[]).map((l, i) => (
                    <tr key={i} className="hover:bg-[var(--bg)]">
                      <td className="py-2.5 px-3 font-mono text-xs text-[var(--tx3)]">{l.matricule}</td>
                      <td className="py-2.5 px-3 text-[var(--tx2)]">{l.prenom} {l.nom}</td>
                      <td className="py-2.5 px-3 font-mono text-xs text-[var(--tx3)]">{l.ipresId || '—'}</td>
                      <td className="py-2.5 px-3 text-right font-medium text-[var(--brand-dark)]">{fmtXOF(l.salaireBase)}</td>
                      <td className="py-2.5 px-3 text-right text-[#7C3AED]">{fmtXOF(l.ipresRgSalarie)}</td>
                      <td className="py-2.5 px-3 text-right text-[#D97706]">{fmtXOF(l.ipresRgPatronal)}</td>
                      <td className="py-2.5 px-3 text-right text-[#7C3AED]">{fmtXOF(l.ipresRcSalarie)}</td>
                      <td className="py-2.5 px-3 text-right text-[#D97706]">{fmtXOF(l.ipresRcPatronal)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-[var(--bg)] border-t-2 border-[var(--bd)]">
                    <td colSpan={3} className="py-3 px-3 font-bold text-[var(--tx3)] uppercase text-xs">Total</td>
                    <td className="py-3 px-3 text-right font-bold text-[var(--brand-dark)]">{fmtXOF(data.totaux.salaireBase)}</td>
                    <td colSpan={2} className="py-3 px-3 text-right font-bold text-[#7C3AED]">
                      Salarié : {fmtXOF(data.totaux.totalSalarie)}
                    </td>
                    <td colSpan={2} className="py-3 px-3 text-right font-bold text-[#D97706]">
                      Patronal : {fmtXOF(data.totaux.totalPatronal)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// ============================================================================
//  Onglet DIPE
// ============================================================================

function DipeTab() {
  const [periodId, setPeriodId] = useState<string | null>(null)
  const notify = useNotificationStore(s => s.addNotification)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['declaration-dipe', periodId],
    queryFn:  () => hrApi.payroll.getDeclarationDipe(periodId!),
    enabled:  !!periodId,
  })

  // DIPE n'a pas d'export CSV côté backend → on génère côté client
  const downloadCsv = () => {
    if (!data) return
    const headers = ['Matricule', 'Nom', 'Prénom', 'NINEA', 'Salaire brut', 'IRPP', 'TRIMF', 'CFCE', 'Total']
    const rows = (data.lignes as any[]).map(l => [
      l.matricule, l.nom, l.prenom, l.ninea,
      l.salaireBrut, l.irpp, l.trimf, l.cfce, l.total,
    ])
    const csv = [
      headers.join(';'),
      ...rows.map(r => r.join(';')),
      '',
      `Totaux;;;;${data.totaux.salaireBrut};${data.totaux.irpp};${data.totaux.trimf};${data.totaux.cfce};${data.totaux.total}`,
    ].join('\n')

    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' })  // BOM pour Excel
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href     = url
    a.download = `DIPE-${data.periode}.csv`
    a.click()
    URL.revokeObjectURL(url)
    notify({ type: 'success', message: 'CSV téléchargé' })
  }

  return (
    <div className="space-y-4">
      <PeriodSelector value={periodId} onChange={setPeriodId} />

      {isLoading && (
        <div className="flex items-center justify-center h-40">
          <RefreshCw className="w-8 h-8 text-[var(--blue)] animate-spin" />
        </div>
      )}

      {isError && (
        <div className="bg-[#FEF2F2] border border-[#DC2626]/30 rounded-xl p-4 text-sm text-[#DC2626]">
          {(error as any)?.response?.data?.message || 'Erreur de chargement'}
        </div>
      )}

      {data && (
        <>
          <div className="bg-[var(--bg)] border border-[var(--bd)] rounded-xl p-5 flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-[var(--brand-amber)] flex items-center justify-center">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-base font-bold text-[var(--brand-dark)]">DIPE — {data.periode}</h3>
                <p className="text-xs text-[var(--tx3)]">Destinataire : {data.organisme} · Échéance : {data.echeance}</p>
              </div>
            </div>
            <button
              onClick={downloadCsv}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--brand-amber)] hover:bg-[#4F3573] text-sm font-medium text-white rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              Télécharger CSV
            </button>
          </div>

          <div className="grid grid-cols-4 gap-4">
            <StatCard label="Total IRPP" value={fmtXOF(data.totaux.irpp)} color="#7C3AED" />
            <StatCard label="Total TRIMF" value={fmtXOF(data.totaux.trimf)} color="var(--blue-d)" />
            <StatCard label="Total CFCE" value={fmtXOF(data.totaux.cfce)} color="#D97706" />
            <StatCard label="Total à reverser" value={fmtXOF(data.totaux.total)} color="#DC2626" highlight />
          </div>

          <div className="bg-white border border-[var(--bd)] rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--bd)] bg-[var(--bg)] text-xs text-[var(--tx3)] uppercase tracking-wider">
                    <th className="text-left  py-3 px-3 font-medium">Matricule</th>
                    <th className="text-left  py-3 px-3 font-medium">Employé</th>
                    <th className="text-left  py-3 px-3 font-medium">NINEA</th>
                    <th className="text-right py-3 px-3 font-medium">Salaire brut</th>
                    <th className="text-right py-3 px-3 font-medium">IRPP</th>
                    <th className="text-right py-3 px-3 font-medium">TRIMF</th>
                    <th className="text-right py-3 px-3 font-medium">CFCE</th>
                    <th className="text-right py-3 px-3 font-medium">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--bd)]">
                  {(data.lignes as any[]).map((l, i) => (
                    <tr key={i} className="hover:bg-[var(--bg)]">
                      <td className="py-2.5 px-3 font-mono text-xs text-[var(--tx3)]">{l.matricule}</td>
                      <td className="py-2.5 px-3 text-[var(--tx2)]">{l.prenom} {l.nom}</td>
                      <td className="py-2.5 px-3 font-mono text-xs text-[var(--tx3)]">{l.ninea || '—'}</td>
                      <td className="py-2.5 px-3 text-right font-medium text-[var(--brand-dark)]">{fmtXOF(l.salaireBrut)}</td>
                      <td className="py-2.5 px-3 text-right text-[#7C3AED]">{fmtXOF(l.irpp)}</td>
                      <td className="py-2.5 px-3 text-right text-[var(--blue-d)]">{fmtXOF(l.trimf)}</td>
                      <td className="py-2.5 px-3 text-right text-[#D97706]">{fmtXOF(l.cfce)}</td>
                      <td className="py-2.5 px-3 text-right font-bold text-[#DC2626]">{fmtXOF(l.total)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-[var(--bg)] border-t-2 border-[var(--bd)]">
                    <td colSpan={3} className="py-3 px-3 font-bold text-[var(--tx3)] uppercase text-xs">Total</td>
                    <td className="py-3 px-3 text-right font-bold text-[var(--brand-dark)]">{fmtXOF(data.totaux.salaireBrut)}</td>
                    <td className="py-3 px-3 text-right font-bold text-[#7C3AED]">{fmtXOF(data.totaux.irpp)}</td>
                    <td className="py-3 px-3 text-right font-bold text-[var(--blue-d)]">{fmtXOF(data.totaux.trimf)}</td>
                    <td className="py-3 px-3 text-right font-bold text-[#D97706]">{fmtXOF(data.totaux.cfce)}</td>
                    <td className="py-3 px-3 text-right font-bold text-[#DC2626]">{fmtXOF(data.totaux.total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// ============================================================================
//  Onglet Récap annuel
// ============================================================================

function RecapTab() {
  const currentYear = new Date().getFullYear()
  const [year, setYear] = useState(currentYear)
  const notify = useNotificationStore(s => s.addNotification)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['recap-annuel', year],
    queryFn:  () => hrApi.payroll.getRecapAnnuel(year),
  })

  const downloadCsv = () => {
    if (!data) return
    const headers = [
      'Matricule', 'Nom', 'Prénom',
      'Salaire brut annuel', 'IRPP', 'TRIMF', 'CFCE',
      'IPRES RG salarié', 'IPRES RG patronal',
      'CSS PF', 'CSS AT', 'Net annuel',
    ]
    const rows = (data.employes as any[]).map(e => [
      e.matricule, e.nom, e.prenom,
      e.salaireBrut, e.irpp, e.trimf, e.cfce,
      e.ipresRgSal, e.ipresRgPat, e.cssPf, e.cssAt, e.netPayable,
    ])
    const csv = [
      headers.join(';'),
      ...rows.map(r => r.join(';')),
    ].join('\n')

    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href     = url
    a.download = `Recap-Annuel-${data.annee}.csv`
    a.click()
    URL.revokeObjectURL(url)
    notify({ type: 'success', message: 'CSV téléchargé' })
  }

  // Liste des années sélectionnables
  const years = useMemo(() => {
    const yrs: number[] = []
    for (let y = currentYear; y >= currentYear - 5; y--) yrs.push(y)
    return yrs
  }, [currentYear])

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">Année</label>
        <select
          value={year}
          onChange={e => setYear(parseInt(e.target.value))}
          className="w-full max-w-xs px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:border-[var(--blue)]"
        >
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center h-40">
          <RefreshCw className="w-8 h-8 text-[var(--blue)] animate-spin" />
        </div>
      )}

      {isError && (
        <div className="bg-[#FEF3C7] border border-[#F59E0B]/30 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-[#D97706]" />
          <p className="text-sm text-[#92400E]">
            {(error as any)?.response?.data?.message || `Aucune période verrouillée pour l'année ${year}.`}
          </p>
        </div>
      )}

      {data && (
        <>
          <div className="bg-[var(--bg)] border border-[var(--bd)] rounded-xl p-5 flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-[var(--blue)] flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-base font-bold text-[var(--brand-dark)]">État récapitulatif {data.annee}</h3>
                <p className="text-xs text-[var(--tx3)]">Échéance : {data.echeance} · {data.effectif} employé(s)</p>
              </div>
            </div>
            <button
              onClick={downloadCsv}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[#2DAFD9] text-sm font-medium text-white rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              Télécharger CSV
            </button>
          </div>

          <div className="bg-white border border-[var(--bd)] rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--bd)] bg-[var(--bg)] text-xs text-[var(--tx3)] uppercase tracking-wider">
                    <th className="text-left  py-3 px-3 font-medium">Matricule</th>
                    <th className="text-left  py-3 px-3 font-medium">Employé</th>
                    <th className="text-right py-3 px-3 font-medium">Brut annuel</th>
                    <th className="text-right py-3 px-3 font-medium">IRPP</th>
                    <th className="text-right py-3 px-3 font-medium">TRIMF</th>
                    <th className="text-right py-3 px-3 font-medium">IPRES sal.</th>
                    <th className="text-right py-3 px-3 font-medium">CSS</th>
                    <th className="text-right py-3 px-3 font-medium">CFCE</th>
                    <th className="text-right py-3 px-3 font-medium">Net annuel</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--bd)]">
                  {(data.employes as any[]).map((e: any, i: number) => (
                    <tr key={i} className="hover:bg-[var(--bg)]">
                      <td className="py-2.5 px-3 font-mono text-xs text-[var(--tx3)]">{e.matricule}</td>
                      <td className="py-2.5 px-3 text-[var(--tx2)]">{e.prenom} {e.nom}</td>
                      <td className="py-2.5 px-3 text-right font-medium text-[var(--brand-dark)]">{fmtXOF(e.salaireBrut)}</td>
                      <td className="py-2.5 px-3 text-right text-[#7C3AED]">{fmtXOF(e.irpp)}</td>
                      <td className="py-2.5 px-3 text-right text-[var(--blue-d)]">{fmtXOF(e.trimf)}</td>
                      <td className="py-2.5 px-3 text-right text-[#7C3AED]">{fmtXOF(e.ipresRgSal)}</td>
                      <td className="py-2.5 px-3 text-right text-[#D97706]">{fmtXOF(e.cssPf + e.cssAt)}</td>
                      <td className="py-2.5 px-3 text-right text-[#D97706]">{fmtXOF(e.cfce)}</td>
                      <td className="py-2.5 px-3 text-right font-bold text-[#059669]">{fmtXOF(e.netPayable)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// ============================================================================
//  Composant utilitaire
// ============================================================================

function StatCard({
  label, value, color, highlight,
}: {
  label: string; value: string; color: string; highlight?: boolean
}) {
  return (
    <div className={`bg-white border rounded-xl p-4 ${highlight ? 'border-[#DC2626]/40 shadow-sm' : 'border-[var(--bd)]'}`}>
      <p className="text-xs text-[var(--tx3)]">{label}</p>
      <p className="text-xl font-bold mt-1" style={{ color }}>{value}</p>
    </div>
  )
}
